
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthentication } from './useAuthentication';
import { useOrganizations } from './useOrganizations';
import { AuthContextType } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuthentication();
  const { 
    organizations, 
    currentOrganization, 
    setCurrentOrganization, 
    refreshOrganizations, 
    retryCount, 
    MAX_RETRIES,
    isFounder,
    isPrimaryFounder,
    userHasAccess
  } = useOrganizations(auth.user);
  
  // Add a ref to track initial auth check
  const initialAuthCheckDone = useRef(false);
  const refreshInProgress = useRef(false);

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        auth.setSession(session);
        auth.setUser(session?.user ?? null);
        
        if (session?.user && !refreshInProgress.current) {
          refreshInProgress.current = true;
          await refreshOrganizations();
          refreshInProgress.current = false;
        } else if (!session?.user) {
          setCurrentOrganization(null);
        }
        
        auth.setIsLoading(false);
        initialAuthCheckDone.current = true;
      }
    );

    // Only do initial session fetch if we haven't done it yet
    if (!initialAuthCheckDone.current) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        auth.setSession(session);
        auth.setUser(session?.user ?? null);
        
        if (session?.user && !refreshInProgress.current) {
          refreshInProgress.current = true;
          refreshOrganizations().finally(() => {
            refreshInProgress.current = false;
          });
        }
        
        auth.setIsLoading(false);
        initialAuthCheckDone.current = true;
      });
    }

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Modified effect for retrying - only runs when retryCount changes
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (retryCount.current > 0 && retryCount.current <= MAX_RETRIES && auth.user && !refreshInProgress.current) {
      // Use exponential backoff for retries
      const timeout = 1000 * Math.pow(2, retryCount.current - 1);
      console.log(`Scheduling retry in ${timeout}ms`);
      
      timer = setTimeout(() => {
        if (!refreshInProgress.current) {
          refreshInProgress.current = true;
          refreshOrganizations().finally(() => {
            refreshInProgress.current = false;
          });
        }
      }, timeout);
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [retryCount.current, auth.user]);

  const value: AuthContextType = {
    user: auth.user,
    session: auth.session,
    organizations,
    currentOrganization,
    isLoading: auth.isLoading,
    signIn: auth.signIn,
    signUp: auth.signUp,
    signOut: auth.signOut,
    setCurrentOrganization,
    refreshOrganizations,
    isFounder,
    isPrimaryFounder,
    userHasAccess
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
